import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Row, 
  Col,
  Button,
  Card,
  Table,
  Space,
  message,
  Spin,
  Tag,
  DatePicker
} from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import FilterPanel from '../../components/common/FilterPanel';
import { warehouseService } from '../../services/warehouseService';
import { itemService } from '../../services/itemService';
import { TaskService } from '../../services/taskService';
import { Feedback } from './task';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FEEDBACK_REASON_OPTIONS: { value: any; label: string }[] = [
  { value: 'levelNotMatch', label: 'taskManagement.list.feedbackModal.reason.levelNotMatch' },
  { value: 'itemOrientationWrong', label: 'taskManagement.list.feedbackModal.reason.itemOrientationWrong' },
  { value: 'spaceNotEnough', label: 'taskManagement.list.feedbackModal.reason.spaceNotEnough' },
  { value: 'stockNotEnough', label: 'taskManagement.list.feedbackModal.reason.stockNotEnough' },
];

interface FeedbackSearchForm {
  warehouseCode?: string;
  orderCode?: string;
  itemCode?: string;
  reason?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  createTimeStart?: number;
  createTimeEnd?: number;
  currentPage: number;
  pageSize: number;
}

const FeedbackList4StorePage: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [itemOptions, setItemOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0
  });

  // 加载仓库和商品选项
  useEffect(() => {
    // loadOptions();
    // 页面初始化时加载数据
    handleSearch({ currentPage: 1, pageSize: 20 });
  }, []);

  // const loadOptions = async () => {
  //   setLoading(true);
  //   try {
  //     const [warehouseRes, itemRes] = await Promise.all([
  //       warehouseService.listWarehouse({}),
  //       itemService.listItems({})
  //     ]);

  //     if (warehouseRes.success && warehouseRes.data) {
  //       const options = warehouseRes.data.map((warehouse: any) => ({
  //         value: warehouse.code,
  //         label: `${warehouse.code} - ${warehouse.name}`
  //       }));
  //       setWarehouseOptions(options);
  //     }

  //     if (itemRes.success && itemRes.data) {
  //       const options = itemRes.data.map((item: any) => ({
  //         value: item.code,
  //         label: `${item.code} - ${item.name}`
  //       }));
  //       setItemOptions(options);
  //     }
  //   } catch (error) {
  //     console.error('Failed to load options:', error);
  //     message.error(t('common.loadFailed'));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // 搜索反馈数据
  const handleSearch = async (values: FeedbackSearchForm) => {
    setSearchLoading(true);
    try {        
        // 构造分页参数
         let searchParams: any = {
          ...values,
          currentPage: values.currentPage || pagination.currentPage,
          pageSize: values.pageSize || pagination.pageSize
        };
        
        // 如果有日期范围，转换为时间戳
        if (values.dateRange && values.dateRange.length === 2) {
          const [startDate, endDate] = values.dateRange;
          searchParams.createTimeStart = startDate ? startDate.startOf('day').valueOf() : undefined;
          searchParams.createTimeEnd = endDate ? endDate.endOf('day').valueOf() : undefined;
        }
        
        // 移除日期范围字段，避免传递给后端
        delete searchParams.dateRange;
        
        const response = await TaskService.pageTaskFeedback4Store(searchParams);
        if (response.success) {
          setFeedbackList(response.data.tableData || []);
          setPagination({
            currentPage: response.data.paging?.currentPage || 1,
            pageSize: values.pageSize || pagination.pageSize,
            total: response.data.paging?.totalCount || 0
          });
          // message.success(t('common.success'));
        } else {
          setFeedbackList([]);
          message.warning(t('taskManagement.list.feedbackModal.loadFailed'));
        }
    } catch (error) {
      console.error('Failed to search feedback:', error);
      message.error(error.message);
      setFeedbackList([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    // 重置后查询所有数据
    handleSearch({ currentPage: 1, pageSize: 20 });
  };

  const handleTableChange = (page: number, pageSize?: number) => {
    form.validateFields().then(values => {
      handleSearch({ 
        ...values, 
        currentPage: page, 
        pageSize: pageSize || pagination.pageSize 
      });
    });
  };

  const handlePageSizeChange = (_: number, pageSize: number) => {
    form.validateFields().then(values => {
      handleSearch({ 
        ...values, 
        currentPage: 1, 
        pageSize: pageSize
      });
    });
  };

  // 表格列定义
  const columns = [
    // {
    //   title: t('taskManagement.list.columns.shop'),
    //   dataIndex: 'warehouseCode',
    //   key: 'warehouseCode',
    // },
    // {
    //   title: t('taskManagement.list.columns.shopName'),
    //   dataIndex: 'warehouseName',
    //   key: 'warehouseName',
    // },
    {
      title: t('taskManagement.list.columns.taskCode'),
      dataIndex: 'orderCode',
      key: 'orderCode',
      fixed: 'left' as const,
    },
     {
      title: t('taskManagement.list.columns.displayGroupName'),
      dataIndex: 'displayGroupName',
      key: 'displayGroupName',
    },
    {
      title: t('taskManagement.list.columns.divisionName'),
      dataIndex: 'divisionName',
      key: 'divisionName',
    },
    {
      title: t('taskManagement.list.feedbackModal.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 120,
    },
    {
      title: t('taskManagement.list.feedbackModal.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 150,
      render: (_, record) => (
        <span>{record.itemDTO?.name || '-'}</span>
      ),
    },
    {
      title: t('taskManagement.list.columns.seqOnLayer'),
      dataIndex: 'seqOnLayer',
      key: 'seqOnLayer',
      width: 120,
    },
    {
      title: t('taskManagement.list.columns.fromLayerCode'),
      dataIndex: 'fromLayerCode',
      key: 'fromLayerCode',
      width: 150,
    },
    {
      title: t('taskManagement.list.columns.fromLayerNumber'),
      dataIndex: 'fromLayerNumber',
      key: 'fromLayerNumber',
      width: 150,
    },
    {
      title: t('taskManagement.list.columns.action'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (_, record) => {
        if (record.action) {
          switch (record.action) {
            case 'IN':
              return <span>{t('taskManagement.details.tabs.moveIn')}</span>;
            case 'OUT':
              return <span>{t('taskManagement.details.tabs.moveOut')}</span>;
            case 'REARRANGE':
              return <span>{t('taskManagement.details.tabs.rearrange')}</span>;
            case 'NEW_ITEM':
              return <span>{t('taskManagement.details.tabs.newItem')}</span>;
            default:
              return <span>{record.action}</span>;
          }
        }
        return <span>{record.action || '-'}</span>;
      },
    },
    {
      title: t('taskManagement.list.columns.planNum'),
      dataIndex: 'planNum',
      key: 'planNum',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.plan4x'),
      dataIndex: 'plan4x',
      key: 'plan4x',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.plan4y'),
      dataIndex: 'plan4y',
      key: 'plan4y',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.plan4z'),
      dataIndex: 'plan4z',
      key: 'plan4z',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.actualNum'),
      dataIndex: 'actualNum',
      key: 'actualNum',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.actual4x'),
      dataIndex: 'actual4x',
      key: 'actual4x',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.actual4y'),
      dataIndex: 'actual4y',
      key: 'actual4y',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.actual4z'),
      dataIndex: 'actual4z',
      key: 'actual4z',
      width: 100,
    },
    {
      title: t('taskManagement.list.feedbackModal.columns.worker'),
      dataIndex: 'workerName',
      key: 'workerName',
      width: 120,
      render: (_, record) => (
        <span>{record.workerName} ({record.workerAccount})</span>
      ),
    },
    {
      title: t('taskManagement.list.feedbackModal.columns.reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (reason: string) => (
       <>{t('taskManagement.list.feedbackModal.reason.' + reason) || reason || '-'}</>
      ),
    },
    {
      title: t('taskManagement.list.feedbackModal.columns.fulfillType'),
      dataIndex: 'fulfillType',
      key: 'fulfillType',
      width: 100,
      render: (fulfillType: string) => (
        <Tag color={fulfillType === 'Complete' ? 'green' : 'orange'}>
          {fulfillType === 'cantFulfill' 
            ? t('taskManagement.list.feedbackModal.cantFulfill')
            : t('taskManagement.list.feedbackModal.fulftypePartial')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.gmtCreate'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.finishTime'),
      dataIndex: 'finishTime',
      key: 'finishTime',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.rejectTime'),
      dataIndex: 'rejectTime',
      key: 'rejectTime',
      width: 180,
      render: (timestamp: number) => formatTime(timestamp),
    },
    // {
    //   title: t('taskManagement.list.feedbackModal.columns.images'),
    //   dataIndex: 'feedbackImagesList',
    //   key: 'feedbackImagesList',
    //   width: 150,
    //   render: (feedbackImagesList: string[]) => (
    //     <span>{feedbackImagesList ? feedbackImagesList.length : 0} {t('taskManagement.list.feedbackModal.images')}</span>
    //   ),
    // },
  ];

   const formatTime = (timestamp: number) => {
      return timestamp ? dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss") : '';
    };

  return (
    <div style={{ padding: '24px' }}>
      {/* 搜索区域 */}
    <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        // initialValues={{}}
        title={t('menu.feedbackList')}
        collapsible={true}
        loading={searchLoading}
    >
        
        <Col span={8}>
            <Form.Item 
            name="orderCode" 
            label={t('taskManagement.list.columns.taskCode')}
            >
            <Input 
                placeholder={t('taskManagement.search.placeholders.taskCode')} 
            />
            </Form.Item>
        </Col>

        <Col span={8}>
            <Form.Item 
            name="reason" 
            label={t('taskManagement.list.feedbackModal.reasonLabel')}
            >
            <Select 
                placeholder={t('taskManagement.list.feedbackModal.reasonLabel')}
                showSearch={false}
                filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loading}
            >
                {FEEDBACK_REASON_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                    {t(option.label)}
                </Option>
                ))}
            </Select>
            </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item 
            name="dateRange" 
            label={t('storeItemSale.list.filter.createTime')}
          >
            <RangePicker
              style={{ width: '100%' }}
              placeholder={[t('storeItemSale.list.filter.startTime'), t('storeItemSale.list.filter.endTime')]}
            />
          </Form.Item>
        </Col>
        {/* <Col span={8}>
            <Form.Item 
            name="itemresCode" 
            label={t('taskManagement.list.feedbackModal.columns.itemCode')}
            >
            <Select 
                placeholder={t('taskManagement.list.feedbackModal.columns.itemCode')}
                showSearch
                filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loading}
            >
                {itemOptions.map(option => (
                <Option key={option.value} value={option.value} label={option.label}>
                    {option.label}
                </Option>
                ))}
            </Select>
            </Form.Item>
        </Col> */}
    </FilterPanel>

    {/* 结果列表 */}
    <Spin spinning={searchLoading}>
        <Table
        columns={columns}
        dataSource={feedbackList}
        rowKey={(record) => `${record.id}`}
        pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handlePageSizeChange
        }}
        scroll={{ x: 'max-content' }}
        style={{ marginTop: '24px' }}
        />
    </Spin>
    </div>
  );
};

export default FeedbackList4StorePage;